import { useState } from 'react';
import { Download, Share, Plus, X } from 'lucide-react';
import { useInstallPrompt } from '../lib/pwa';

const DISMISS_KEY = 'pmd-candidat-install-dismissed';

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Bannière « Télécharger l'app » : bouton d'installation natif sur Android/Chrome
 * (via `beforeinstallprompt`) et, sur iOS, instructions pour l'ajout à l'écran
 * d'accueil (Safari n'expose pas d'installation programmable).
 */
export default function InstallAppBanner() {
  const { canInstall, promptInstall, installed, ios } = useInstallPrompt();
  const [dismissed, setDismissed] = useState<boolean>(wasDismissed);
  const [iosOpen, setIosOpen] = useState(false);

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // stockage indisponible : on masque juste pour la session.
    }
  };

  // Rien à proposer si déjà installée, ou si aucun canal d'installation.
  if (installed || dismissed) return null;
  if (!canInstall && !ios) return null;

  return (
    <>
      <div className="bg-indigo-600 text-white">
        <div className="max-w-6xl mx-auto px-6 py-2.5 flex items-center gap-3">
          <Download size={18} className="flex-shrink-0" />
          <p className="text-sm font-medium flex-1 min-w-0">
            Installez l'application Permio sur votre téléphone
          </p>
          <button
            onClick={() => (canInstall ? void promptInstall() : setIosOpen(true))}
            className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-white text-indigo-700 text-sm font-semibold hover:bg-indigo-50 transition-colors"
          >
            Télécharger l'app
          </button>
          <button
            onClick={dismiss}
            className="flex-shrink-0 p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Fermer"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Instructions iOS */}
      {iosOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center"
          onClick={() => setIosOpen(false)}
        >
          <div
            className="bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Installer l'application</h3>
              <button onClick={() => setIosOpen(false)} aria-label="Fermer">
                <X size={18} className="text-slate-400" />
              </button>
            </div>
            <ol className="space-y-4 text-sm text-slate-700">
              <li className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold flex-shrink-0">
                  1
                </span>
                <span className="flex items-center gap-1.5">
                  Touchez <Share size={16} className="text-indigo-600" /> « Partager » dans la barre Safari
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold flex-shrink-0">
                  2
                </span>
                <span className="flex items-center gap-1.5">
                  Choisissez <Plus size={16} className="text-indigo-600" /> « Sur l'écran d'accueil »
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold flex-shrink-0">
                  3
                </span>
                <span>Validez : l'app apparaît sur votre écran d'accueil.</span>
              </li>
            </ol>
            <button
              onClick={() => setIosOpen(false)}
              className="mt-6 w-full py-2.5 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors"
            >
              J'ai compris
            </button>
          </div>
        </div>
      )}
    </>
  );
}
